// src/hooks/useSocket.js
import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import api from "../../services/api";

const rawSocketUrl = process.env.REACT_APP_SOCKET_URL;

// Parse the URL to properly split the domain and the path.
// This ensures Socket.IO hits the correct Nginx path (like /api/socket.io) 
// instead of defaulting to /socket.io on the base domain and hitting a 404.
let baseUrl = rawSocketUrl;
let socketPath = "/socket.io";

try {
  const urlObj = new URL(rawSocketUrl);
  baseUrl = urlObj.origin;
  socketPath = urlObj.pathname === '/' ? '/socket.io' : `${urlObj.pathname.replace(/\/$/, '')}/socket.io`;
} catch (e) {
  console.warn("Invalid socket URL, falling back to defaults");
}

const useSocket = (userId, callbacks = {}) => {
  const socketRef = useRef(null);

  useEffect(() => {
    // ❌ no user → no socket
    if (!userId) return;

    // ❌ already connected → do nothing
    if (socketRef.current) return;

    const socket = io(baseUrl, {
      path: socketPath,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      timeout: 20000,
    });

    socketRef.current = socket;

    // =====================
    // CONNECTION
    // =====================
    socket.on("connect", () => {
      console.log("🔌 Socket connected:", socket.id);

      // ✅ tell server user is ONLINE + name
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const userName =
        storedUser?.name ||
        storedUser?.firstName ||
        storedUser?.profile?.basicInfo?.firstName ||
        "User";

      socket.emit("user-connect", {
        userId,
        userName,
      });

      callbacks.onConnect?.();
    });


    // =====================
    // SAFE LISTENERS
    // =====================
    if (callbacks.onConnectError)
      socket.on("connect_error", callbacks.onConnectError);

    if (callbacks.onDisconnect)
      socket.on("disconnect", callbacks.onDisconnect);

    if (callbacks.onOnlineUsers)
      socket.on("online-users", callbacks.onOnlineUsers);

    if (callbacks.onUserOnline)
      socket.on("user-online", callbacks.onUserOnline);

    if (callbacks.onUserOffline)
      socket.on("user-offline", callbacks.onUserOffline);

    if (callbacks.onNewMessage)
      socket.on("new-message", callbacks.onNewMessage);

    if (callbacks.onTyping)
      socket.on("typing", callbacks.onTyping);

    if (callbacks.onStopTyping)
      socket.on("stop-typing", callbacks.onStopTyping);

    if (callbacks.onInterestAccepted)
      socket.on("interest_accepted", callbacks.onInterestAccepted);

    if (callbacks.onInterestRejected)
      socket.on("interest_rejected", callbacks.onInterestRejected);

    if (callbacks.onInterestCancelled)
      socket.on("interest_cancelled", callbacks.onInterestCancelled);
    // 🔔 REAL-TIME NOTIFICATIONS
    if (callbacks.onNewNotification)
      socket.on("new-notification", callbacks.onNewNotification);

    // =====================
    // CLEANUP (LOGOUT / TAB CLOSE)
    // =====================
    return () => {
      if (!socketRef.current) return;

      // 🔴 tell server user is OFFLINE
      socketRef.current.emit("user-offline", { userId });

      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;

      console.log("🧹 Socket cleaned up");
    };
  }, [userId]);

  // =====================
  // EXPOSED METHODS
  // =====================
  return {
    socket: socketRef.current,

    isConnected: () => socketRef.current?.connected || false,

    joinConversation: (conversationId) => {
      socketRef.current?.emit("join-conversation", {
        conversationId,
        userId,
      });
    },

    leaveConversation: (conversationId) => {
      socketRef.current?.emit("leave-conversation", {
        conversationId,
        userId,
      });
    },

    sendMessage: (conversationId, text, image) => {
      socketRef.current?.emit("send-message", {
        conversationId,
        text,
        image,
      });
    },

    sendTyping: (conversationId) => {
      socketRef.current?.emit("typing", { conversationId });
    },

    sendStopTyping: (conversationId) => {
      socketRef.current?.emit("stop-typing", { conversationId });
    },
  };
};

export default useSocket;
